import { createFileRoute } from "@tanstack/react-router";
import { BarChart3, LineChart, TrendingUp } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/analytics-hero.webp";
import cardsImg from "@/assets/analytics-cards.webp";
import teamImg from "@/assets/analytics-team.webp";

const IMG: Record<string, string> = { hero: heroImg, cards: cardsImg, team: teamImg };

export const Route = createFileRoute("/vi/platform/analytics")({
  head: () => ({
    meta: [
      { title: "Phân tích & Báo cáo — banana.cards" },
      { name: "description", content: "Bảng điều khiển thời gian thực cho thẻ, liên hệ, deal, hợp đồng và đặt lịch. Biết chính xác điều gì đang hiệu quả." },
      { property: "og:title", content: "Phân tích & Báo cáo — banana.cards" },
      { property: "og:description", content: "Bảng điều khiển thời gian thực cho thẻ, liên hệ, deal, hợp đồng và đặt lịch. Biết chính xác điều gì đang hiệu quả." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/analytics", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Phân tích & Báo cáo"
      title="Xem điều gì hiệu quả —"
      italic="đến từng lần chạm."
      description="Bảng điều khiển thời gian thực cho thẻ, liên hệ, deal, hợp đồng và đặt lịch. Biết thẻ hiệu quả nhất, nhân viên hiệu quả nhất và sự kiện hiệu quả nhất."
      heroImage={IMG['hero']}
      metrics={[{ v: "Thời gian thực", l: "dữ liệu" }, { v: "30+", l: "báo cáo" }, { v: "API", l: "xuất" }]}
      bullets={[
        { icon: BarChart3, title: "Bảng điều khiển dựng sẵn", desc: "Thẻ, lead, pipeline, hợp đồng và đặt lịch — có sẵn." },
        { icon: TrendingUp, title: "Cohort & phễu", desc: "Theo dõi mỗi lead từ lần chạm đầu đến deal chốt." },
        { icon: LineChart, title: "Báo cáo tùy chỉnh", desc: "Tự xây với bộ lọc, phân khúc và view đã lưu." }
      ]}
      deepDives={[
        { eyebrow: "Phân tích thẻ", title: "Mọi lần chạm,", italic: "có nguồn gốc.", description: "Xem thiết kế thẻ, kênh và sự kiện nào đẩy pipeline nào — theo người dùng, đội và vùng.", image: IMG["cards"], reverse: false, points: ["Lượt xem và lưu theo thẻ", "Phân bổ theo kênh", "Theo dõi lead theo sự kiện", "Truyền tham số UTM"] },
        { eyebrow: "Đội ngũ", title: "Huấn luyện bằng", italic: "dữ liệu.", description: "Bảng điều khiển quản lý hiển thị vận tốc, thời gian phản hồi, và nơi nhân viên cần giúp.", image: IMG["team"], reverse: true, points: ["Hiệu suất theo nhân viên", "SLA thời gian phản hồi", "Heatmap hoạt động", "Email tóm tắt hàng tuần"] }
      ]}
      howItWorks={[
        { title: "Kết nối dữ liệu", desc: "Banana thu thập mọi thứ tự nhiên." },
        { title: "Chọn bảng điều khiển", desc: "Hoặc xây view tùy chỉnh." },
        { title: "Cắt và phân khúc", desc: "Theo đội, vùng, kênh hoặc chiến dịch." },
        { title: "Chia sẻ & xuất", desc: "CSV, API và email tóm tắt theo lịch." }
      ]}
      capabilities={[
        "Bảng điều khiển thời gian thực",
        "Phân tích cohort & phễu",
        "Báo cáo tùy chỉnh & view đã lưu",
        "Email tóm tắt theo lịch",
        "Xuất CSV & API",
        "Phân quyền theo đội",
        "Truyền tham số UTM",
        "View huấn luyện quản lý"
      ]}
      faq={[
        { q: "Banana thực sự đo gì?", a: "Lượt xem thẻ và khách truy cập duy nhất, chuyển đổi lead (lần chạm biến thành liên hệ thu thập), nguồn truy cập hàng đầu (Direct, Instagram, LinkedIn, Google, WhatsApp, Email, chiến dịch gắn UTM, v.v.), quốc gia/vùng/thành phố, loại thiết bị, trình duyệt và OS, cộng với tương tác từng khối trên thẻ (link nào được nhấp, CTA nào chuyển đổi)." },
        { q: "Dữ liệu có thời gian thực không?", a: "Có — lượt xem, chạm và sự kiện lead stream vào bảng điều khiển khi chúng xảy ra. Biểu đồ làm mới trong vài giây và bạn có thể chuyển khoảng thời gian (7 ngày, 30 ngày, 90 ngày, 1 năm, mọi thời điểm) mà không cần tải lại trang." },
        { q: "Có thể xem phân tích theo thẻ hoặc thành viên đội không?", a: "Có — đào sâu vào thẻ riêng để xem lượt xem, nguồn và chuyển đổi, hoặc cuộn lên theo thành viên đội để so sánh hiệu suất giữa các nhân viên, môi giới hoặc stylist. Admin thấy toàn workspace; thành viên thấy thẻ của họ." },
        { q: "Có theo dõi UTM và liên kết lượt xem với chiến dịch cụ thể không?", a: "Có — bất kỳ link tới thẻ nào đều hỗ trợ tham số UTM chuẩn (source, medium, campaign, content, term). Banana thu thập và nhóm để bạn thấy chính xác quảng cáo Instagram, bài LinkedIn hoặc chiến dịch QR in nào đẩy lượt xem và lead." },
        { q: "Có xuất dữ liệu thô hoặc đẩy vào GA4 / kho dữ liệu không?", a: "Có — xuất bất kỳ view nào dưới dạng CSV, hoặc kéo sự kiện từ REST API theo lịch vào Google Analytics 4, Mixpanel, PostHog, Snowflake hoặc BigQuery. Webhook bắn theo mỗi sự kiện để truyền dẫn thời gian thực." },
        { q: "Vị trí được phát hiện thế nào, và có chính xác không?", a: "Vị trí suy ra từ IP khách lúc xem — chính xác đến cấp thành phố/vùng cho hầu hết trường hợp, cấp quốc gia khi khác. Chúng tôi không bao giờ thu tọa độ GPS chính xác và không lưu IP thô." }
      ]}
      outro={ { title: "Biết điều gì hiệu quả.", italic: "Làm nhiều hơn.", description: "Phân tích thời gian thực trên mọi sản phẩm Banana." } }
    />
  );
}